import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@mui/material';
import { useSelectedCompanyCategories, useShowMenuByCategories, useURLparams } from '@hooks';
import { FullWidthContent, MainMenuLayout } from '@layouts';
import { Table } from '@molecules';
import { dateToIsoString } from '@utils';
import { COLUMNS, ITEMS_FOOTER } from './constants';
import useConsumptionByCommerce from './data/useConsumptionByCommerce';
import { consumptionByCommerceFilterConsumptions } from './events';
import SearchBoxConsumptionByCommerce from './modules/SearchBoxConsumptionByCommerce';

export interface SearchBoxParams {
  toDate?: Date;
  fromDate?: Date;
  product?: string;
  areaId?: string;
}

/**
 * @function ConsumptionByCommerce
 * @description This screen is used to display the consumption by commerce.
 * @returns JSX.Element
 */
const ConsumptionByCommerce = () => {
  const { t } = useTranslation();
  const { params, setURLparams } = useURLparams();
  const navigate = useNavigate();
  const paramsTyped = params as Record<string, string>;
  const { isLoading: isLoadingCategories } = useSelectedCompanyCategories();
  const canViewConsuptionByCommerce = useShowMenuByCategories();
  const { data, isLoading: isLoadingConsumptionByCommerce } = useConsumptionByCommerce(paramsTyped);
  const isLoading = isLoadingConsumptionByCommerce || isLoadingCategories;

  /**
   * @function handleOnChangePage
   * @description This function is used to handle the change of page in the table.
   */
  const handleOnChangePage = (params: SearchBoxParams) => {
    const newParams: Record<string, string> = {
      ...(params?.product && { product: params.product }),
      ...(params?.fromDate && { fromDate: dateToIsoString(params.fromDate) }),
      ...(params?.toDate && { toDate: dateToIsoString(params.toDate) }),
      ...(params?.areaId && { areaId: params.areaId }),
    };
    consumptionByCommerceFilterConsumptions(newParams);
    setURLparams(newParams);
  };

  const itemsFooter = data && data?.total && data?.page?.length > 0 ? ITEMS_FOOTER(data.total) : [];

  if (!isLoadingCategories && !canViewConsuptionByCommerce) {
    navigate('/home');
  } else {
    return (
      <MainMenuLayout>
        <FullWidthContent>
          <SearchBoxConsumptionByCommerce
            isLoading={isLoading}
            params={paramsTyped}
            onSubmit={handleOnChangePage}
            dataLength={data?.page?.length}
          />
          <Alert severity='info'>{t('CONSUMPTION:WARNING')}</Alert>
          <Table
            isLoading={isLoading}
            items={data?.page}
            columns={COLUMNS}
            withPagination={false}
            itemsFooter={itemsFooter}
            noResultsText={!paramsTyped?.product ? t('CONSUMPTION:NOT_SELECTED_PRODUCT') : ''}
          />
        </FullWidthContent>
      </MainMenuLayout>
    );
  }
};

export default ConsumptionByCommerce;
